import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '@/store';
import { useToastContext } from '@/contexts/ToastContext';
import { adminDashboardService } from '@/api/adminServices';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Shield, Send, Upload, ChevronLeft } from 'lucide-react';

const EmailBroadcastPage: React.FC = () => {
  const { user } = useAppStore();
  const navigate = useNavigate();
  const { showError, showSuccess } = useToastContext();

  const [loadingGuard, setLoadingGuard] = useState(true);
  const [sendingAll, setSendingAll] = useState(false);
  const [uploadSending, setUploadSending] = useState(false);

  const [allSubject, setAllSubject] = useState('');
  const [allMessage, setAllMessage] = useState('');

  const [excelSubject, setExcelSubject] = useState('');
  const [excelMessage, setExcelMessage] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string>('');

  const validateFile = (f: File | null): string => {
    if (!f) return 'Please select an Excel .xlsx file';
    const isXlsx = f.name.toLowerCase().endsWith('.xlsx');
    if (!isXlsx) return 'Invalid file type. Please upload an .xlsx file';
    if (f.size === 0) return 'Selected file is empty';
    return '';
  };

  useEffect(() => {
    if (!user) return;
    if (!user.is_staff) {
      showError('Access denied. Staff privileges required.');
      navigate('/dashboard');
    } else {
      setLoadingGuard(false);
    }
  }, [user, navigate, showError]);

  const handleSendAll = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedSubject = allSubject.trim();
    const trimmedMessage = allMessage.trim();
    if (!trimmedSubject || !trimmedMessage) { showError('Subject and message are required'); return; }
    if (trimmedSubject.length < 3) { showError('Subject must be at least 3 characters long'); return; }
    if (trimmedMessage.length < 10) { showError('Message must be at least 10 characters long'); return; }
    try {
      setSendingAll(true);
      console.log('[EmailBroadcast] Sending to all users:', {
        subjectLength: trimmedSubject.length,
        messageLength: trimmedMessage.length
      });
      const res = await adminDashboardService.sendEmailToAllUsers({ subject: trimmedSubject, message: trimmedMessage });
      console.log('[EmailBroadcast] SendAll response:', res);
      if (res && (res as any).success !== false) {
        const msg = (res as any).detail || res?.message || 'Emails are being sent to all users';
        showSuccess(msg);
        setAllSubject('');
        setAllMessage('');
      } else {
        showError(res?.message || (res as any)?.detail || 'Backend did not confirm success');
      }
    } catch (err: any) {
      const resp = err?.response;
      const respData = resp?.data;
      const detail = (respData && (respData.detail || respData.message)) || err?.message;
      console.error('[EmailBroadcast] SendAll error:', {
        status: resp?.status,
        data: respData,
        message: err?.message
      });
      showError(typeof respData === 'string' ? respData : (detail || 'Failed to send emails to all users'));
    } finally {
      setSendingAll(false);
    }
  };

  const handleSendFromExcel = async (e: React.FormEvent) => {
    e.preventDefault();
    const errText = validateFile(file);
    if (errText) { setFileError(errText); showError(errText); return; }
    if (!excelSubject.trim() || !excelMessage.trim()) {
      showError('Subject and message are required');
      return;
    }
    try {
      setUploadSending(true);
      const res = await adminDashboardService.sendEmailFromExcel({ file: file!, subject: excelSubject.trim(), message: excelMessage.trim() });
      if (res && (res as any).success !== false) {
        showSuccess(res?.message || 'Emails are being sent to uploaded recipients');
      } else {
        showError(res?.message || 'Backend did not confirm success');
      }
      setExcelSubject('');
      setExcelMessage('');
      setFile(null);
      setFileError('');
    } catch (err: any) {
      const resp = err?.response;
      const respData = resp?.data;
      const detail = (respData && (respData.detail || respData.message)) || err?.message;
      console.error('[EmailBroadcast] SendFromExcel error:', {
        status: resp?.status,
        data: respData,
        message: err?.message
      });
      showError(typeof respData === 'string' ? respData : (detail || 'Failed to send emails from Excel'));
    } finally {
      setUploadSending(false);
    }
  };

  if (loadingGuard) {
    return <LoadingSpinner message="Verifying access..." fullScreen />;
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="p-6 max-w-4xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate('/admin')}
            className="p-2 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <Shield className="w-6 h-6 text-indigo-600" />
          <div>
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white">Email Broadcast</h1>
            <p className="text-gray-600 dark:text-gray-400 text-sm">Send announcements to all users or a list from Excel</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Send to All Users */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 lg:p-6 border border-gray-200 dark:border-gray-700">
            <h2 className="text-base lg:text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Send className="w-4 h-4 text-blue-600" />
              Send to All Users
            </h2>
            <form onSubmit={handleSendAll} className="space-y-3">
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Subject</label>
                <input
                  type="text"
                  value={allSubject}
                  onChange={(e) => setAllSubject(e.target.value)}
                  placeholder="🎉 MiPT Announcement"
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Message</label>
                <textarea
                  value={allMessage}
                  onChange={(e) => setAllMessage(e.target.value)}
                  rows={6}
                  placeholder="We're excited to announce new features launching this week!..."
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <button
                type="submit"
                disabled={
                  sendingAll ||
                  !allSubject.trim() ||
                  !allMessage.trim() ||
                  allSubject.trim().length < 3 ||
                  allMessage.trim().length < 10
                }
                className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                {sendingAll ? 'Sending...' : 'Send to All Users'}
              </button>
            </form>
          </div>

          {/* Send from Excel */}
          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 lg:p-6 border border-gray-200 dark:border-gray-700">
            <h2 className="text-base lg:text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <Upload className="w-4 h-4 text-emerald-600" />
              Send from Excel (.xlsx)
            </h2>
            <form onSubmit={handleSendFromExcel} className="space-y-3">
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Excel File</label>
                <input
                  type="file"
                  accept=".xlsx"
                  onChange={(e) => {
                    const next = e.target.files && e.target.files[0] ? e.target.files[0] : null;
                    setFile(next);
                    setFileError(validateFile(next));
                  }}
                  className={`w-full p-2 border ${fileError ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white`}
                />
                <div className="mt-1 text-xs">
                  <p className="text-gray-500">Excel must have a header "Email" in column A; "Name" optional in column B.</p>
                  {fileError && <p className="text-red-600 mt-1">{fileError}</p>}
                </div>
              </div>
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Subject</label>
                <input
                  type="text"
                  value={excelSubject}
                  onChange={(e) => setExcelSubject(e.target.value)}
                  placeholder="Welcome to MiPT!"
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Message</label>
                <textarea
                  value={excelMessage}
                  onChange={(e) => setExcelMessage(e.target.value)}
                  rows={6}
                  placeholder={"Thank you for joining us. Here's what to expect next..."}
                  className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <button
                type="submit"
                disabled={uploadSending || !!validateFile(file) || !excelSubject.trim() || !excelMessage.trim()}
                className="w-full px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                {uploadSending ? 'Sending...' : 'Send Emails from Excel'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailBroadcastPage;


